import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert, Form } from "@themesberg/react-bootstrap";
import { Row, Col } from "@themesberg/react-bootstrap";
import axios from 'axios';
import { BASE_URL } from '../../constants';

const GlobalShelterSettings = () => {
  const [absentDays, setAbsentDays] = useState('');
  const [loadingAbsentDays, setLoadingAbsentDays] = useState(false);
  const [successAbsentDays, setSuccessAbsentDays] = useState(false);
  const [errorAbsentDays, setErrorAbsentDays] = useState(null);
  const [absentDaysDifferences, setAbsentDaysDifferences] = useState(null);

  const [autoCheckoutHours, setAutoCheckoutHours] = useState('');
  const [loadingAutoCheckout, setLoadingAutoCheckout] = useState(false);
  const [successAutoCheckout, setSuccessAutoCheckout] = useState(false);
  const [errorAutoCheckout, setErrorAutoCheckout] = useState(null);
  const [autoCheckoutDifferences, setAutoCheckoutDifferences] = useState(null);

  // Fetch Absent Days
  useEffect(() => {
    const fetchAbsentDays = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/shelters/absent-days-state`);
        setAbsentDays(res.data.absentDays);
        setAbsentDaysDifferences(res.data.differences || null);
      } catch (err) {
        console.error(err);
        setErrorAbsentDays('Failed to fetch absent days.');
      }
    };
    fetchAbsentDays();
  }, []);

  // Fetch Auto Checkout Hours
  useEffect(() => {
    const fetchAutoCheckout = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/shelters/auto-checkout-hours-state`);
        setAutoCheckoutHours(res.data.autoCheckoutHours);
        setAutoCheckoutDifferences(res.data.differences || null);
      } catch (err) {
        console.error(err);
        setErrorAutoCheckout('Failed to fetch auto-checkout hours.');
      }
    };
    fetchAutoCheckout();
  }, []);

  // Save Absent Days
  const handleSaveAbsentDays = async () => {
    setLoadingAbsentDays(true);
    setSuccessAbsentDays(false);
    setErrorAbsentDays(null);
    try {
      await axios.put(`${BASE_URL}/shelters/absent-days`, { absentDays: Number(absentDays) });

      // Refresh
      const res = await axios.get(`${BASE_URL}/shelters/absent-days-state`);
      setAbsentDays(res.data.absentDays);
      setAbsentDaysDifferences(res.data.differences || null);

      setSuccessAbsentDays(true);
      setTimeout(() => setSuccessAbsentDays(false), 3000);
    } catch (err) {
      console.error(err);
      setErrorAbsentDays('Failed to update absent days.');
    } finally {
      setLoadingAbsentDays(false);
    }
  };

  // Save Auto Checkout Hours
  const handleSaveAutoCheckout = async () => {
    setLoadingAutoCheckout(true);
    setSuccessAutoCheckout(false);
    setErrorAutoCheckout(null);
    try {
      await axios.put(`${BASE_URL}/shelters/auto-checkout-hours`, { autoCheckoutHours: Number(autoCheckoutHours) });

      // Refresh
      const res = await axios.get(`${BASE_URL}/shelters/auto-checkout-hours-state`);
      setAutoCheckoutHours(res.data.autoCheckoutHours);
      setAutoCheckoutDifferences(res.data.differences || null);

      setSuccessAutoCheckout(true);
      setTimeout(() => setSuccessAutoCheckout(false), 3000);
    } catch (err) {
      console.error(err);
      setErrorAutoCheckout('Failed to update auto-checkout hours.');
    } finally {
      setLoadingAutoCheckout(false);
    }
  };

  return (
    <Row>
      {/* Absent Days */}
      <Col md={6}>
        <div className="d-flex align-items-center gap-2 mb-4">
          <Form.Label className="mb-0"><strong>Absent Days:</strong></Form.Label>
          <Form.Control
            type="number"
            min="1"
            max="1"
            value={absentDays}
            onChange={(e) => setAbsentDays(e.target.value)}
            disabled={loadingAbsentDays}
            style={{ width: 100 }}
          />
          <Button
            onClick={handleSaveAbsentDays}
            disabled={loadingAbsentDays || absentDays === ""}
          >
            {loadingAbsentDays ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </div>
        {successAbsentDays && <Alert variant="success">Absent days updated!</Alert>}
        {errorAbsentDays && <Alert variant="danger">{errorAbsentDays}</Alert>}
        {absentDaysDifferences && absentDaysDifferences.length > 0 && (
          <Alert
            variant="warning"
            style={{ maxHeight: '150px', overflowY: 'auto' }}
          >
            <strong>Different values found in:</strong>
            <div>
              {absentDaysDifferences.map((diff, idx) => (
                <div key={idx}>{diff}</div>
              ))}
            </div>
          </Alert>
        )}
      </Col>

      {/* Auto Checkout Hours */}
      <Col md={6}>
        <div className="d-flex align-items-center gap-2 mb-4">
          <Form.Label className="mb-0"><strong>Auto Checkout Hours:</strong></Form.Label>
          <Form.Control
            type="number"
            min="1"
            max="1"
            value={autoCheckoutHours}
            onChange={(e) => setAutoCheckoutHours(e.target.value)}
            disabled={loadingAutoCheckout}
            style={{ width: 100 }}
          />
          <Button
            onClick={handleSaveAutoCheckout}
            disabled={loadingAutoCheckout || autoCheckoutHours === ""}
          >
            {loadingAutoCheckout ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </div>
        {successAutoCheckout && <Alert variant="success">Auto-checkout hours updated!</Alert>}
        {errorAutoCheckout && <Alert variant="danger">{errorAutoCheckout}</Alert>}
        {autoCheckoutDifferences && autoCheckoutDifferences.length > 0 && (
          <Alert
            variant="warning"
            style={{ maxHeight: '150px', overflowY: 'auto' }}
          >
            <strong>Different values found in:</strong>
            <div>
              {autoCheckoutDifferences.map((diff, idx) => (
                <div key={idx}>{diff}</div>
              ))}
            </div>
          </Alert>
        )}
      </Col>
    </Row>
  );
};

export default GlobalShelterSettings;
